import React from "react";
import { Button, Card, Col, DatePicker, Descriptions, Form, Input, Row, Space, Switch, Tag } from "antd";
import { CheckOutlined, CloseOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const DatesField = ({ mode }) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const isViewMode = mode === "VIEW";

  if (isViewMode) {
    const closedDates = form.getFieldValue(["country", "closedDates"]) || [];

    return (
      <Row gutter={16}>
        <Col span={24}>
          <Card title={t("countries.closed_dates")}>
            {closedDates.length === 0 ? (
              <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                {t("countries.no_closed_dates_configured")}
              </div>
            ) : (
              closedDates.map((closedDate, index) => (
                <Card key={index} type="inner" style={{ marginBottom: 16 }}>
                  <Descriptions column={{ xs: 1, sm: 2, md: 4 }} size="small">
                    <Descriptions.Item label={t("countries.date")}>
                      {closedDate.date ? dayjs(closedDate.date).format("DD-MM-YYYY") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("countries.closed_date_name")}>
                      {closedDate.name || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("countries.is_public_holiday")}>
                      {closedDate.isPublicHoliday ? (
                        <Tag color="green" icon={<CheckOutlined />}>
                          {t("yes")}
                        </Tag>
                      ) : (
                        <Tag icon={<CloseOutlined />}>{t("no")}</Tag>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("countries.impacts_delivery")}>
                      {closedDate.impactsDelivery ? (
                        <Tag color="green" icon={<CheckOutlined />}>
                          {t("yes")}
                        </Tag>
                      ) : (
                        <Tag icon={<CloseOutlined />}>{t("no")}</Tag>
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              ))
            )}
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <Row gutter={16}>
      <Col span={24}>
        <Card
          title={t("countries.closed_dates")}
          extra={
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => {
                const currentDates = form.getFieldValue(["country", "closedDates"]) || [];
                form.setFieldsValue({
                  country: {
                    closedDates: [
                      ...currentDates,
                      {
                        date: null,
                        name: "",
                        isPublicHoliday: false,
                        impactsDelivery: false,
                      },
                    ],
                  },
                });
              }}
            >
              {t("countries.add_closed_date")}
            </Button>
          }
        >
          <Form.List name={["country", "closedDates"]}>
            {(fields, { remove }) => (
              <>
                {fields.map((field) => {
                  const dateValue = form.getFieldValue(["country", "closedDates", field.name, "date"]);
                  const nameValue = form.getFieldValue(["country", "closedDates", field.name, "name"]);

                  return (
                    <Card
                      key={field.key}
                      type="inner"
                      title={
                        <Space>
                          <span>{nameValue || t("countries.closed_date")}</span>
                          {dateValue && (
                            <span style={{ fontWeight: "normal", color: "#999" }}>
                              ({dayjs(dateValue).format("DD-MM-YYYY")})
                            </span>
                          )}
                        </Space>
                      }
                      extra={
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)}>
                          {t("delete")}
                        </Button>
                      }
                      style={{ marginBottom: 16 }}
                    >
                      <Row gutter={16}>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item
                            {...field}
                            name={[field.name, "date"]}
                            label={t("countries.date")}
                            rules={[{ required: true, message: t("required") }]}
                            getValueProps={(value) => ({
                              value: value ? dayjs(value) : null,
                            })}
                            normalize={(value) => (value ? value.format("YYYY-MM-DD") : null)}
                          >
                            <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <Form.Item
                            {...field}
                            name={[field.name, "name"]}
                            label={t("countries.closed_date_name")}
                            rules={[{ required: true, message: t("required") }]}
                          >
                            <Input placeholder={t("countries.closed_date_name_placeholder")} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={3}>
                          <Form.Item
                            {...field}
                            name={[field.name, "isPublicHoliday"]}
                            label={t("countries.is_public_holiday")}
                            valuePropName="checked"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={3}>
                          <Form.Item
                            {...field}
                            name={[field.name, "impactsDelivery"]}
                            label={t("countries.impacts_delivery")}
                            valuePropName="checked"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
                {fields.length === 0 && (
                  <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                    {t("countries.no_closed_dates_configured")}
                  </div>
                )}
              </>
            )}
          </Form.List>
        </Card>
      </Col>
    </Row>
  );
};

export default DatesField;
